import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, Loader2, PowerOff, Power } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", description: "", active: true };

export function BusinessUnitsTab() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const { sorted: sortedBus, sort: buSort, setSort: setBuSort } = useSortable(items);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | doc
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org/business-units");
      // Sort ascending by code (case-insensitive, natural)
      const sorted = [...(data || [])].sort((a, b) =>
        (a.code || "").localeCompare(b.code || "", undefined, { numeric: true, sensitivity: "base" })
      );
      setItems(sorted);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (bu) => { setEditing(bu); setForm({ name: bu.name, code: bu.code, description: bu.description || "", active: bu.active }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/org/business-units", form);
      else await api.put(`/org/business-units/${editing.id}`, form);
      toast.success("Saved");
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (bu) => {
    if (!window.confirm(`Delete "${bu.name}"? This can't be undone.`)) return;
    try { await api.delete(`/org/business-units/${bu.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (bu) => {
    const nextActive = !bu.active;
    if (!nextActive && !window.confirm(
      `Deactivate "${bu.name}"? It will disappear from dropdowns and dashboards but no data is lost.`
    )) return;
    try {
      await api.put(`/org/business-units/${bu.id}`, { active: nextActive });
      toast.success(nextActive ? `${bu.code} activated` : `${bu.code} deactivated`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Business Units</h3>
          <p className="text-xs text-slate-500">{items.length} configured. Records scope permissions and reporting.</p>
        </div>
        {can("org_mgmt", "create") && (
          <Button data-testid="bu-new-btn" onClick={openNew} className="bg-gbe hover:bg-gbe-hover text-white h-9">
            <Plus className="w-4 h-4 mr-1.5" /> New Business Unit
          </Button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <SortableTh label="Code" k="code" sort={buSort} setSort={setBuSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="Name" k="name" sort={buSort} setSort={setBuSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="Group" k="group" sort={buSort} setSort={setBuSort} className="text-left font-medium px-5 py-2.5" />
            <SortableTh label="Users" k="user_count" sort={buSort} setSort={setBuSort} className="text-right font-medium px-5 py-2.5" />
            <SortableTh label="Sites" k="site_count" sort={buSort} setSort={setBuSort} className="text-right font-medium px-5 py-2.5" />
            <SortableTh label="Status" k="active" sort={buSort} setSort={setBuSort} className="text-right font-medium px-5 py-2.5" />
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="p-4"><div className="h-6 rounded shimmer" /></td></tr>
          ) : sortedBus.map((bu) => (
            <tr key={bu.id} data-testid={`bu-row-${bu.code}`} className="border-b border-slate-100 hover:bg-slate-50/60">
              <td className="px-5 py-3 font-mono text-xs font-semibold text-gbe">{bu.code}</td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-800">{bu.name}</span>
                </div>
              </td>
              <td className="px-5 py-3 text-slate-600 text-xs">{bu.group || "—"}</td>
              <td className="px-5 py-3 text-right tabular-nums text-slate-700">{bu.user_count}</td>
              <td className="px-5 py-3 text-right tabular-nums text-slate-700">{bu.site_count}</td>
              <td className="px-5 py-3 text-right">
                {(() => {
                  const isActive = !!bu.active;
                  const label = !isActive && bu.planned_from
                    ? `Planned · ${bu.planned_from}`
                    : (isActive ? "Active" : "Inactive");
                  const cls = isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : (bu.planned_from
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-slate-100 text-slate-500 border-slate-200");
                  return (
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}>
                      {label}
                    </span>
                  );
                })()}
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                {can("org_mgmt", "edit") && !bu.active && (
                  <Button
                    data-testid={`bu-activate-${bu.code}`}
                    onClick={() => toggleActive(bu)}
                    variant="ghost"
                    size="sm"
                    className="h-8 text-emerald-700 hover:bg-emerald-50 mr-1"
                    title="Activate this Business Unit"
                  >
                    <Power className="w-3.5 h-3.5 mr-1" /> Activate
                  </Button>
                )}
                {can("org_mgmt", "edit") && bu.active && (
                  <Button
                    data-testid={`bu-deactivate-${bu.code}`}
                    onClick={() => toggleActive(bu)}
                    variant="ghost"
                    size="sm"
                    className="h-8 text-slate-500 hover:bg-slate-50"
                    title="Deactivate this Business Unit"
                  >
                    <PowerOff className="w-3.5 h-3.5" />
                  </Button>
                )}
                {can("org_mgmt", "edit") && (
                  <Button data-testid={`bu-edit-${bu.code}`} onClick={() => openEdit(bu)} variant="ghost" size="sm" className="h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                )}
                {can("org_mgmt", "delete") && (
                  <Button data-testid={`bu-del-${bu.code}`} onClick={() => del(bu)} variant="ghost" size="sm" className="h-8 text-rose-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing === "new" ? "New Business Unit" : `Edit ${editing?.name || ""}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-[1fr_120px] gap-3">
              <div>
                <Label>Name</Label>
                <Input data-testid="bu-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Code</Label>
                <Input data-testid="bu-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={16} className="mt-1 font-mono" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5 bg-slate-50/60">
              <div>
                <Label className="text-sm">Active</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  When off, this BU is hidden from dropdowns, dashboards and Sales-by-BU splits.
                </p>
              </div>
              <Switch
                data-testid="bu-form-active"
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="bu-form-save" onClick={save} disabled={saving} className="bg-gbe hover:bg-gbe-hover text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
